import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
// @ts-expect-error — ChatbotWithContext not yet in installed chatbot types (pre-existing issue)
import { ChatbotWithContext } from '@wealthlane/chatbot';
import { toast } from 'react-toastify';
import ToastComponent from '@/components/toast';
import AuthService from '@/services/authService';
import useWealthlaneCustomerApi from '@/hooks/useWealthlaneCustomerApi';
import useInstance from '@/hooks/custom-hooks/useInstance';
import useGetCustomerUserDetails from '@/hooks/get/useGetCustomerUserDetails';
import useGetCurrentTenantInfoEnabled from '@/hooks/get/useGetCurrentTenantInfoEnabled';
import { axiosInstance } from '@/services/apiInterceptor';
import { configState } from '@/components/ingest';
import { getUserName } from '@/utils/global';

/**
 * Self-contained chatbot overlay. Fetches its own data
 * (user details + tenant info) and renders nothing until ready.
 * No props needed — fully autonomous inside AuthGate.
 *
 * react-query cache sharing means no duplicate requests when
 * CustomerNavbar already fetched the same data.
 */
const ChatbotOverlay: React.FC = () => {
  const navigate = useNavigate();
  const authService = new AuthService();
  const { handleRequest: handleCustomerRequest } = useWealthlaneCustomerApi();
  const { getRoleId, getCurrentCid } = useInstance();
  const cid = getCurrentCid();

  const { data: userName } = useGetCustomerUserDetails(handleCustomerRequest);
  const { data: currentTenantInfo } = useGetCurrentTenantInfoEnabled(handleCustomerRequest, true);

  if (!currentTenantInfo || !userName) return null;

  return (
    <AnimatePresence initial={false} mode="wait">
      <div className="relative">
        <ChatbotWithContext
          axiosInstance={axiosInstance}
          config={configState}
          tenantIdAccessor={() => currentTenantInfo?.id}
          langfusePublicKey={process.env.REACT_APP_LANGFUSE_PUBLIC_KEY!}
          langfuseEndpointUrl={process.env.REACT_APP_LANGFUSE_BASE_URL!}
          accessTokenAccessor={() => authService.getToken()}
          customerName={getUserName(userName)}
          sourceRoleId={getRoleId()}
          companyId={cid?.toString?.() ?? ''}
          httpUrl={process.env.REACT_APP_CHATBOT_REST_URL!}
          wsUrl={process.env.REACT_APP_CHATBOT_WEBSOCKET_API!}
          alertFunctionSuccess={(message: string) => {
            toast.success(<ToastComponent label="Success" message={message} />);
          }}
          alertFunctionError={(message: string) => {
            toast.error(<ToastComponent label="Error" message={message} />);
          }}
          navigate={navigate}
          allowMention={false}
          transcribeAudioLink={process.env.REACT_APP_CHATBOT_AUDIO_TRANSCRIBE_URL || ''}
          cid={cid.toString() ?? ''}
        />
      </div>
    </AnimatePresence>
  );
};

export default ChatbotOverlay;
